/**
 * CombatRoundBar — sticky top bar shown while combat is active.
 *
 * Spec: `.kiro/specs/active-effects-round-tracker/design.md` § Component:
 * `CombatRoundBar`. Renders round counter, health readout, advisory action
 * chips, effects row (combat states + active effects), and End Round /
 * End Combat buttons.
 */

import React, { useState } from 'react';
import { useCharacterStore } from '../../stores/character';
import type { ActionCounters, ActiveEffect } from '../../types/combatRound';
import { HALF_ACTION_BUDGET, FULL_ACTION_BUDGET, REACTION_BUDGET } from '../../types/combatRound';
import { getStateDefinition } from '../../data/combatStateDefinitions';
import { EffectChipPopover } from './EffectChipPopover';

const COUNTER_META: Array<{
  kind: keyof ActionCounters;
  label: string;
  budget: number | null;
}> = [
  { kind: 'halfActionsUsed', label: 'Half', budget: HALF_ACTION_BUDGET },
  { kind: 'fullActionsUsed', label: 'Full', budget: FULL_ACTION_BUDGET },
  { kind: 'reactionsUsed', label: 'React', budget: REACTION_BUDGET },
  { kind: 'freeActionsUsed', label: 'Free', budget: null }
];

const formatRounds = (r: number | null): string => (r === null ? '∞' : `${r}r`);

export function CombatRoundBar() {
  const isInCombat = useCharacterStore((s) => s.isInCombat);
  const roundNumber = useCharacterStore((s) => s.roundNumber);
  const actionCounters = useCharacterStore((s) => s.actionCounters);
  const endRound = useCharacterStore((s) => s.endRound);
  const endCombat = useCharacterStore((s) => s.endCombat);
  const wounds = useCharacterStore((s) => s.character?.progression?.woundsTracking);
  const fatigue = useCharacterStore((s) => s.character?.progression?.fatigueTracking);
  const combatStates = useCharacterStore((s) => s.combatStates);
  const activeEffects = useCharacterStore((s) => s.activeEffects);
  const [openEffectId, setOpenEffectId] = useState<string | null>(null);

  if (!isInCombat) return null;

  const combatStateChips = Object.values(combatStates).map((cs) => {
    const def = getStateDefinition(cs.id);
    return {
      key: `cs:${cs.id}`,
      icon: def?.icon ?? '●',
      label: def?.name ?? cs.id,
      rounds: cs.roundsRemaining,
      source: def?.description ?? cs.source ?? 'Combat state',
      isActiveEffect: false as const
    };
  });

  const activeEffectChips = Object.values(activeEffects).map((e: ActiveEffect) => ({
    key: `ae:${e.id}`,
    icon: '•',
    label: e.label,
    rounds: e.roundsRemaining,
    source: e.source,
    isActiveEffect: true as const,
    effect: e
  }));

  return (
    <div
      className="sticky top-0 z-40 w-full bg-page text-white border-b border-line-strong px-3 py-2 flex flex-wrap items-center gap-3 text-sm shadow"
      role="region"
      aria-label="Combat round tracker"
      data-testid="combat-round-bar"
    >
      <div data-testid="round-counter" className="font-semibold whitespace-nowrap">
        ⏱️ Round {roundNumber}
      </div>

      {(wounds || fatigue) && (
        <div data-testid="health-readout" className="whitespace-nowrap text-ink-dim">
          {wounds && (
            <span>
              W {wounds.current}/{wounds.maximum}
            </span>
          )}
          {wounds && fatigue && <span className="mx-1">•</span>}
          {fatigue && (
            <span>
              F {fatigue.current}/{fatigue.maximum}
            </span>
          )}
        </div>
      )}

      <div data-testid="action-counters" className="flex gap-1 flex-wrap">
        {COUNTER_META.map(({ kind, label, budget }) => {
          const used = actionCounters[kind];
          const over = budget !== null && used > budget;
          return (
            <span
              key={kind}
              data-testid={`counter-${kind}`}
              className={`px-2 py-0.5 rounded border ${
                over
                  ? 'border-red-400 text-red-300 bg-red-900/30'
                  : 'border-line-strong text-ink-dim'
              }`}
            >
              {label}: {used}
              {budget !== null ? `/${budget}` : ''}
              {over ? ' ⚠' : ''}
            </span>
          );
        })}
      </div>

      <div data-testid="effects-row" className="flex flex-wrap gap-1 flex-1 min-w-[4rem]">
        {[...combatStateChips, ...activeEffectChips].map((chip) => (
          <div key={chip.key} className="relative">
            <button
              type="button"
              onClick={() => setOpenEffectId(openEffectId === chip.key ? null : chip.key)}
              data-testid={`effect-chip-${chip.key}`}
              className="px-2 py-0.5 rounded border border-indigo-400 text-indigo-100 bg-indigo-900/30 hover:bg-indigo-900/60"
            >
              {chip.icon} {chip.label} {formatRounds(chip.rounds)}
            </button>
            {openEffectId === chip.key && (
              <EffectChipPopover
                label={chip.label}
                source={chip.source}
                rounds={chip.rounds}
                effect={chip.isActiveEffect ? chip.effect : undefined}
                combatStateId={chip.isActiveEffect ? undefined : chip.key.slice(3)}
                onClose={() => setOpenEffectId(null)}
              />
            )}
          </div>
        ))}
      </div>

      <div data-testid="bar-buttons" className="flex gap-2 ml-auto">
        <button
          type="button"
          onClick={endRound}
          data-testid="end-round-button"
          className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold"
        >
          End Turn
        </button>
        <button
          type="button"
          onClick={endCombat}
          data-testid="end-combat-button"
          className="px-3 py-1 rounded border border-red-400 text-red-200 hover:bg-red-900/40"
        >
          End Combat
        </button>
      </div>
    </div>
  );
}
